import { splitValue } from '@/shared/lib';

import { MarketRepository } from '../api';
import type { TopMoverItem } from '../api/fetcher';
import {
  normalizeUpbitMarkets,
  normalizeUpbitTicker,
  normalizeBithumbMarkets,
  normalizeCoinoneMarkets,
  normalizeCoinoneTicker,
  normalizeBithumbTicker,
  normalizeBinanceMarkets,
  normalizeBinanceTicker,
} from '../lib/normalize';
import { CandleUnitsByRange, MaxCandleCount, TotalCandleCountByMinutes } from '../const';
import { MarketViewModel, TickerViewModel } from '../model';

import type { Exchange, NormalizedMarketItem, NormalizedTicker } from '../types/exchange';
import type { MarketCandle, UpbitMarketCandleResponse, UpbitTicker, UpbitTickerResponse } from '../types';

/** 백엔드 top-movers camelCase → 프론트 UpbitTicker snake_case */
const mapTopMoverToUpbitTicker = (item: TopMoverItem): UpbitTicker => ({
  market: item.market,
  trade_price: item.tradePrice,
  opening_price: item.openingPrice,
  high_price: item.highPrice,
  low_price: item.lowPrice,
  change: item.change as UpbitTicker['change'],
  signed_change_rate: item.signedChangeRate,
  signed_change_price: item.signedChangePrice,
  acc_trade_volume_24h: item.accTradeVolume24h,
  acc_trade_price_24h: item.accTradePrice24h ?? undefined,
});

/**
 * TRAD-2021 — `GET /api/v1/ticker`(멀티 마켓 조회) 실응답은 camelCase(`tradePrice` 등)인데
 * `UpbitTicker`(WS 원본 규격)는 snake_case다. 종전 코드는 `ticker.trade_price` 처럼 snake_case로
 * 직접 읽어 REST 폴백이 **항상 undefined**였다 — 최초 로드는 WS 틱이 이미 도착해 있어 안 보이다가,
 * 네비게이션 등으로 WS 갱신이 잠시 끊기는 순간 REST 폴백이 대신 쓰이며 '-'/'0.00%'로 굳었다
 * (실측: dev `/api/v1/ticker` 응답 전량 camelCase 확인).
 *
 * `TickerViewModel.ticker`(단건 조회)가 쓰는 `d.trade_price ?? d.tradePrice` 양쪽 폴백 패턴을
 * 그대로 재사용한다 — 계약이 나중에 snake_case로 바뀌어도, 혹시 WS 원본이 실수로 이 경로를
 * 타도 깨지지 않는다.
 */
export const mapRestTickerToUpbitTicker = (raw: UpbitTickerResponse): UpbitTicker => {
  const d = raw as unknown as UpbitTickerResponse & {
    tradePrice?: number; openingPrice?: number; highPrice?: number; lowPrice?: number;
    prevClosingPrice?: number; signedChangePrice?: number; signedChangeRate?: number;
    accTradeVolume24h?: number | null; accTradePrice24h?: number | null;
    highest52WeekPrice?: number; highest52WeekDate?: string | number;
    lowest52WeekPrice?: number; lowest52WeekDate?: string;
  };
  return {
    market: d.market,
    trade_price: d.trade_price ?? d.tradePrice,
    opening_price: d.opening_price ?? d.openingPrice,
    high_price: d.high_price ?? d.highPrice,
    low_price: d.low_price ?? d.lowPrice,
    prev_closing_price: d.prev_closing_price ?? d.prevClosingPrice,
    change: d.change,
    change_price: d.change_price,
    signed_change_price: d.signed_change_price ?? d.signedChangePrice,
    signed_change_rate: d.signed_change_rate ?? d.signedChangeRate,
    acc_trade_volume_24h: d.acc_trade_volume_24h ?? d.accTradeVolume24h,
    acc_trade_price_24h: d.acc_trade_price_24h ?? d.accTradePrice24h,
    highest_52_week_price: d.highest_52_week_price ?? d.highest52WeekPrice,
    // 🔴 RawUpbitMarketData.highest_52_week_date 는 기존 타입에도 number 로 선언돼 있다
    // (Upbit REST 실응답이 그런 것으로 보임, 이 함수가 만든 불일치가 아니다) — UpbitTicker 는
    // string 을 기대하므로 문자열로 맞춘다(TickerViewModel.ticker 의 동일 처리와 같은 패턴).
    highest_52_week_date: String(d.highest_52_week_date ?? d.highest52WeekDate ?? ''),
    lowest_52_week_price: d.lowest_52_week_price ?? d.lowest52WeekPrice,
    lowest_52_week_date: d.lowest_52_week_date ?? d.lowest52WeekDate,
  };
};

class MarketService {
  repository: typeof MarketRepository;

  constructor(repository: typeof MarketRepository) {
    this.repository = repository;
  }

  async getMarkets() {
    const markets = await this.repository.findMarkets();
    return markets;
  }

  // 분 단위(unit)로 마켓 캔들을 가져오는 메서드
  // 현재 시간을 기준으로 24시간동안의 데이터를 모두 가져옴
  async getMarketCandlesWithUnit(props: Pick<MarketCandle, 'market' | 'range'>) {
    // 조회 기간에 따라 분 단위를 내부에서 조절해서 계산
    // 조회 기간 : 1일(5분봉), 7일(15분봉), 1달(60분봉), 3달(240분봉)
    const unit = CandleUnitsByRange[props.range!];
    const count = splitValue(TotalCandleCountByMinutes[props.range!], MaxCandleCount);
    const data = await this.getMarketData(props.market, unit, count);

    return new MarketViewModel(data);
  }

  // 한번 캔들을 가져올때 최대 200개씩 가져온다. (counts 배열에 가져올 count를 각 요소 최대 200개씩)
  async getMarketData(market: string, unit: number, counts: number[]) {
    let time = new Date().toISOString();
    const totalCandles: UpbitMarketCandleResponse[] = [];

    for await (const count of counts) {
      const { data, initTime } = await this.repository.findMarketCandlesWithUnit({ market, unit, to: time, count });
      totalCandles.unshift(...data.reverse());
      time = initTime;
    }

    return totalCandles;
  }

  async getMarketTicker(market: string) {
    const data = await this.repository.findMarketTicker(market);
    if (!data?.[0]) return null;

    return new TickerViewModel(data[0]);
  }

  async getMarketTickers(markets: string[]) {
    const data = await this.repository.findMarketTickers(markets);

    // TRAD-2021 — REST 응답(camelCase)을 snake_case(UpbitTicker)로 먼저 정규화한다.
    // 이 매핑 없이 ticker.trade_price/prev_closing_price 를 직접 읽으면 항상 undefined였다.
    return data.map((raw: UpbitTickerResponse) => {
      const ticker = mapRestTickerToUpbitTicker(raw);
      // WebSocket 원본과 동일한 소수 포맷 (예: 0.0235 = 2.35%)
      const rate = ticker.prev_closing_price
        ? ((ticker.trade_price ?? 0) - ticker.prev_closing_price) / ticker.prev_closing_price
        : 0;
      return {
        ...ticker,
        change_rate: rate,
        signed_change_rate: rate,
      };
    });
  }
  // --- Top Movers ---

  async getTopMovers(): Promise<{ topGainers: UpbitTicker[]; topLosers: UpbitTicker[] }> {
    const data = await this.repository.fetchTopMovers();
    return {
      topGainers: data.topGainers.map(mapTopMoverToUpbitTicker),
      topLosers: data.topLosers.map(mapTopMoverToUpbitTicker),
    };
  }

  // --- 멀티 거래소 ---

  private readonly marketNormalizers = {
    UPBIT: normalizeUpbitMarkets,
    BITHUMB: normalizeBithumbMarkets,
    BINANCE: normalizeBinanceMarkets,
    COINONE: normalizeCoinoneMarkets,
  } as const;

  private readonly tickerNormalizers = {
    UPBIT: normalizeUpbitTicker,
    BITHUMB: normalizeBithumbTicker,
    BINANCE: normalizeBinanceTicker,
    COINONE: normalizeCoinoneTicker,
  } as const;

  async getMarketsByExchange(exchange: Exchange): Promise<NormalizedMarketItem[]> {
    const raw = await this.repository.findMarketsByExchange(exchange);
    return this.marketNormalizers[exchange](raw);
  }

  async getMarketTickersByExchange(exchange: Exchange, markets: string[]): Promise<NormalizedTicker[]> {
    const raw = await this.repository.findMarketTickersByExchange(exchange, markets);
    const normalize = this.tickerNormalizers[exchange];
    // TRAD-3635 — 코인원 정규화는 openingPrice 가 없으면 null 이다(등락 0% 를 지어내지 않음). 그 행은 빼고,
    // 그 종목은 WS 첫 프레임(first 가 항상 있음)이 오면 채워진다. 다른 거래소 정규화는 null 을 내지 않는다.
    return raw
      .map((item: Record<string, unknown>) => normalize(item as never))
      .filter((t: NormalizedTicker | null): t is NormalizedTicker => t !== null);
  }
  // --- 도미넌스 ---

  async getDominance() {
    return this.repository.fetchDominance();
  }
}

export default new MarketService(MarketRepository);
